import React, { FC, useMemo } from 'react';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { SummaryItem } from '../../../../../components/summary-list/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { Company } from '../../../../../hooks/index';
import { useFormat } from '../../../../../hooks/use-format.hook';
import { toNumber } from '../../../../../utils/to-number.util';
import {
  EventTransactionAttachedFilesPreview,
  ReverseVestingSummaryRows,
} from '../../components/index';
import { ChildrenFormProps } from '../../event-form.types';
import { CompanyIncorporationDocumentsSummary } from './company-incorporation-documents-summary';

export type CompanyIncorporationFinalStepProps = ChildrenFormProps;

type SummaryTitleProps = {
  titleClassName: string;
  titleWrapperClassName: string;
};

const summaryTitleProps = {
  titleClassName: 'w-fit',
  titleWrapperClassName: 'w-[120px]',
} satisfies SummaryTitleProps;

export const CompanyIncorporationFinalStep: FC<CompanyIncorporationFinalStepProps> = ({
  event,
  formData,
  handleCloseModal,
  nextFormStep,
  prevFormStep,
  isLoading,
}) => {
  const { format } = useFormat();
  const { companyIncorporation } = formData();
  const { selectedCompany: { currency } = {} } = Company.useSelected();

  const parValue = companyIncorporation?.parValue ?? '0';
  const parsedParValue = toNumber(parValue);

  const { issuances, totalShares, sharesByClass, totalConsideredPay } = useMemo(() => {
    const issuanceItems = companyIncorporation?.issuances ?? [];
    const classTotals = new Map<string, { name: string; shares: number }>();
    let sharesSum = 0;

    issuanceItems.forEach(({ shareClass, shares }) => {
      const shareCount = Number(shares ?? 0);
      sharesSum += shareCount;

      if (!shareClass?.id) return;

      const existing = classTotals.get(shareClass.id);
      classTotals.set(shareClass.id, {
        name: shareClass.name,
        shares: (existing?.shares ?? 0) + shareCount,
      });
    });

    return {
      issuances: issuanceItems,
      totalShares: sharesSum,
      sharesByClass: Array.from(classTotals.values()),
      totalConsideredPay: parsedParValue * sharesSum,
    };
  }, [companyIncorporation?.issuances, parsedParValue]);

  return (
    <>
      <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex w-full shrink-0 flex-col gap-4">
          <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
            <span className="px-4 text-sm font-[550] text-gray-700">
              <AppFormattedMessage id="GENERAL" />
            </span>
            <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
              <SummaryItem
                className="border-b-[1px] border-gray-100 pt-0"
                title={<AppFormattedMessage id="INCORPORATION_DATE" />}
                value={
                  companyIncorporation?.incorporationDate
                    ? format(companyIncorporation.incorporationDate, 'dd/MM/yyyy')
                    : '-'
                }
                {...summaryTitleProps}
              />
              <SummaryItem
                className="border-b-[1px] border-gray-100"
                title={<AppFormattedMessage id="TOTAL_SHARES" />}
                value={totalShares.toLocaleString('en-US')}
                {...summaryTitleProps}
              />
              {sharesByClass.map(({ name, shares }) => (
                <SummaryItem
                  className="border-b-[1px] border-gray-100"
                  key={name}
                  title={name}
                  value={shares.toLocaleString('en-US')}
                  {...summaryTitleProps}
                />
              ))}
              <SummaryItem
                className="border-b-[1px] border-gray-100"
                title={<AppFormattedMessage id="SHARE_PAR_VALUE" />}
                value={`${currency?.symbol ?? '$'}${parsedParValue.toLocaleString('en-US', { maximumFractionDigits: 7 })}`}
                {...summaryTitleProps}
              />
              <SummaryItem
                className="pb-0"
                title={<AppFormattedMessage id="TOTAL_CONSIDERED_PAY" />}
                value={`${currency?.symbol ?? '$'}${totalConsideredPay.toLocaleString('en-US', { maximumFractionDigits: 7 })}`}
                {...summaryTitleProps}
              />
            </div>
          </div>

          {issuances.map((issuance, index) => {
            const considerationPay = parsedParValue * Number(issuance.shares ?? 0);

            return (
              <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2" key={index}>
                <Tooltip
                  childrenWrapperClassName="max-w-[500px]"
                  content={issuance.stakeholder.fullName}
                >
                  <span className="truncate px-4 text-sm font-[550] text-gray-700">
                    <AppFormattedMessage id="ISSUANCE" /> - {issuance.stakeholder.fullName}
                  </span>
                </Tooltip>
                <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                  <SummaryItem
                    className="border-b-[1px] border-gray-100 pt-0"
                    title={<AppFormattedMessage id="STAKEHOLDER" />}
                    value={
                      <Tooltip content={issuance.stakeholder.fullName}>
                        {issuance.stakeholder.fullName}
                      </Tooltip>
                    }
                    {...summaryTitleProps}
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="SHARE_CLASS" />}
                    value={
                      <Tooltip content={issuance.shareClass.name}>
                        {issuance.shareClass.name}
                      </Tooltip>
                    }
                    {...summaryTitleProps}
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="SHARES_ISSUANCE" />}
                    value={Number(issuance.shares).toLocaleString('en-US')}
                    {...summaryTitleProps}
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="CONSIDERED_PAY" />}
                    value={`${currency?.symbol ?? '$'}${considerationPay.toLocaleString('en-US', { maximumFractionDigits: 7 })}`}
                    {...summaryTitleProps}
                  />
                  <ReverseVestingSummaryRows
                    hasRowsBelow
                    reverseVesting={issuance.reverseVesting}
                    summaryTitleProps={summaryTitleProps}
                  />
                  <EventTransactionAttachedFilesPreview
                    event={event}
                    files={issuance.files}
                    transactionId={issuance.id}
                  />
                </div>
              </div>
            );
          })}

          <CompanyIncorporationDocumentsSummary
            additionalNotes={companyIncorporation?.additionalNotes}
            files={companyIncorporation?.files}
          />
        </div>
      </div>
      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="ADD_COMPANY_INCORPORATION" />
          </Button>
        </div>
      </div>
    </>
  );
};
